import React, { useEffect, useCallback, useState } from 'react'
import {Button} from '@betareader/design-system';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
// i18n
import { useTranslation } from 'react-i18next';
// redux
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchReaders
} from '../../../modules/reader';

import {
  updateReaderFilters,
  updateVersionFilter,
  clearReadingData,
  clearSurveyData,
  clearReviewData
} from '../../../modules/report';

import ReaderFilter from './readerFilter';
import { authUtil } from '../../../utils';

const ReaderJourneyFilterContainer = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const currentBook = useSelector(state => state.book.currentBook);
  const readers = useSelector(state => state.reader.readers);
  const readerFilters = useSelector(state => state.report.readerFilters) ?? [{}];
  // current version filter. fall back to most recent version if not set
  const versionFilter = useSelector(state => state.report.versionFilter);
  const fetchingReadingData = useSelector(state => state.report.fetchingReadingData);

  const getFiltersFromQueryString = useCallback(() => {
    let filters = undefined;
    const query = queryString.parse(location.search);
    if (query.filters !== undefined) {
      try {
        filters = JSON.parse(query.filters);
      } catch (err) {
        console.error('Failed to parse filter', err);
      }
    }
    return filters;
  }, [location.search]);

  const [showReaderFilters, setShowReaderFilters] = useState(getFiltersFromQueryString() !== undefined);
  const [versionOptions, setVersionOptions] = useState([]);

  useEffect(() => {
    const fetchReadersAsync = async () => {
      const idToken = await authUtil.getFreshIdToken();
      dispatch(fetchReaders(idToken, currentBook._id, [{ key: 'exclude', value: [] }]));
      fetchVersionOptions();
    }
    if (currentBook) {
      fetchReadersAsync();
      // Default to the most recent content version.
      //
      // NOTE: readers stay on the content version they were assigned, which is
      // often NOT the most recent one, and their comments, progress and survey
      // answers belong to that version's parts. So an author whose readers sit
      // on an older version still has to widen the Version filter by hand to
      // see their data — the report looks empty otherwise.
      //
      // Defaulting to every version fixes that, but multiplies the work each
      // report does, so it is deliberately held back until we are confident in
      // the report latency. Deliberate decision, not an oversight.
      const contentIds = (currentBook?.content ?? [])
        .map(contentVersion => contentVersion?._id)
        .filter(Boolean);
      if (contentIds.length > 0) {
        dispatch(updateVersionFilter([contentIds[contentIds.length - 1]]));
      }
    }
  }, [currentBook]);

  const fetchVersionOptions = async () => {
    const idToken = await authUtil.getFreshIdToken();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/reports/settings/version-options?b=${currentBook._id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'br-token': idToken
        }
      });
      if (!response.ok) throw new Error('Failed to load version options');
      const options = await response.json();
      setVersionOptions(options.map((option, index) => ({
        key: option._id,
        text: `v${option.versionNumber || index + 1}`,
        value: option._id,
        description: `${t('Readers')}: ${option.readerCount ?? 0}`
      })));
    } catch (error) {
      console.log('Failed to get version options', error.message);
    }
  }

  const getReaderOptions = useCallback(() => readers?.map(reader => ({
    key: reader._id,
    text: reader.name,
    value: reader._id,
    description: reader.invitation?.status?.toLowerCase() || ''
  })) ?? [], [readers]);

  const addFilter = (index) => {
    const updatedReaderFilters = [...readerFilters];
    updatedReaderFilters.splice(index + 1, 0, {});
    updateFilterList(updatedReaderFilters);
  }

  const removeFilter = (index) => {
    const updatedReaderFilters = [...readerFilters];
    updatedReaderFilters.splice(index, 1);
    if (updatedReaderFilters.length === 0) {
      updatedReaderFilters.push({});
    }
    updateFilterList(updatedReaderFilters);
  }

  const handleFilterChange = ({ index, operator, value }) => {
    const updatedReaderFilters = [...readerFilters];
    updatedReaderFilters[index] = { operator, value };
    dispatch(clearReadingData());
    dispatch(clearSurveyData());
    dispatch(clearReviewData());
    updateFilterList(updatedReaderFilters);
  }

  const handleVersionChange = event => {
    const selected = Array.from(event.target.selectedOptions, option => option.value);
    if (selected.length > 0) {
      // don't allow empty version filters
      dispatch(updateVersionFilter(selected));
    }
  }

  const updateFilterList = (newFilters) => {
    dispatch(updateReaderFilters(newFilters, currentBook._id));
  }

  const getReaderFilterText = useCallback(() => {
    if (readerFilters?.length === 1 && Object.keys(readerFilters[0]).length === 0 && readerFilters[0].constructor === Object) {
      return 'All readers'
    } else if (readerFilters?.length > 0) {
      return `${readerFilters.length} reader filter${readerFilters.length > 1 ? 's' : ''}`
    } else {
      return 'No filters'
    }
  }, [readerFilters]);

  const getVersionFilterText = useCallback(() => {
    if (!versionFilter?.length) return t('Version');
    const versions = versionFilter.map(id => currentBook?.content?.find(content => content._id === id)?.versionNumber)
      .filter(Boolean);
    return versions.length ? versions.map(number => `v${number}`).join(', ') : t('Version');
  }, [versionFilter, currentBook?.content]);

  const getFilterText = () => {
    return `${getVersionFilterText()} | ${getReaderFilterText()}`
  }

  const toggleReaderFilters = () => {
    setShowReaderFilters(!showReaderFilters);
  }

  return (
    <section aria-label={t('Filters')}>
      <Button variant='secondary' disabled={fetchingReadingData}
        aria-expanded={showReaderFilters} aria-controls='reader-journey-filters'
        onClick={toggleReaderFilters}>
        {getFilterText()}
      </Button>
      {showReaderFilters && (
        <div id='reader-journey-filters'>
          <label htmlFor='reader-journey-versions'>{t('Version')}</label>
          <select id='reader-journey-versions' multiple
            value={Array.isArray(versionFilter) ? versionFilter : []}
            onChange={handleVersionChange}>
            {versionOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.text} · {option.description}
              </option>
            ))}
          </select>
          <ul style={{listStyle: 'none', margin: 0, padding: 0}}>
            {readerFilters.map((readerFilter, index) => (
              <ReaderFilter key={index} index={index}
                operator={readerFilter.operator} value={readerFilter.value}
                removeFilter={removeFilter} addFilter={addFilter}
                readerOptions={getReaderOptions()}
                handleFilterChange={handleFilterChange} />
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

// redux stuff
export default ReaderJourneyFilterContainer;
